import { useEffect, useState } from 'react';
import { Loader2, Link2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { presupuestoApi } from '../../utils/api';
import SelectorCuenta, { SelectorCentro } from './SelectorCuenta';

/**
 * Rubros sin cuenta (de un Excel propio o de un presupuesto anterior al modelo estándar)
 * → cuenta y centro. El mapeo se guarda por empresa: la próxima importación ya lo usa.
 */
export default function TabMapeo({ data, catalogo, puedeEditar, onAplicado }) {
  const [info, setInfo] = useState(null);
  const [elecciones, setElecciones] = useState({});
  const [guardando, setGuardando] = useState(false);

  const cargar = async () => {
    try {
      const r = await presupuestoApi.listarMapeos();
      setInfo(r.data.data);
      const previas = {};
      for (const m of r.data.data.mapeos) previas[m.origen] = { cuentaId: m.cuentaId, centroCostoId: m.centroCostoId };
      setElecciones(previas);
    } catch (e) { toast.error(e.response?.data?.error || 'No se pudo cargar el mapeo'); }
  };
  useEffect(() => { cargar(); }, [data.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!info) return <div className="flex items-center justify-center py-16 text-gray-400"><Loader2 className="animate-spin mr-2" /> Cargando…</div>;

  const sinCuentaAqui = data.lineas.filter(l => l.sinCuenta);
  const rubros = [...new Set([...sinCuentaAqui.map(l => l.origen).filter(Boolean), ...info.pendientes.map(p => p.origen)])].sort((a, b) => a.localeCompare(b, 'es'));
  const lineasDe = (origen) => sinCuentaAqui.filter(l => l.origen === origen);
  const elegir = (origen, cambio) => setElecciones({ ...elecciones, [origen]: { ...elecciones[origen], ...cambio } });
  const listos = rubros.filter(o => elecciones[o]?.cuentaId).length;

  const guardar = async () => {
    setGuardando(true);
    try {
      await presupuestoApi.guardarMapeos(rubros.filter(o => elecciones[o]?.cuentaId).map(o => ({ origen: o, ...elecciones[o] })));
      const r = await presupuestoApi.aplicarMapeos(data.id);
      toast.success(`${r.data.data.imputadas} línea(s) quedaron con cuenta`);
      onAplicado(r.data.data.presupuesto);
      cargar();
    } catch (e) { toast.error(e.response?.data?.error || 'No se pudo aplicar el mapeo'); }
    finally { setGuardando(false); }
  };

  if (!rubros.length) {
    return <div className="bg-white border border-gray-200 rounded-xl px-4 py-10 text-center text-gray-500">Todas las líneas tienen cuenta. Los rubros de un Excel propio que no se reconozcan al importarlo aparecerán aquí.</div>;
  }
  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">Estas líneas vienen de un Excel propio o de un presupuesto anterior y todavía no tienen cuenta contable. Asigne una cuenta a cada rubro: se aplica a todas sus líneas y se recuerda para la próxima importación. Sin cuenta no hay real contra qué comparar, y el presupuesto no se puede aprobar.</p>
      <div className="bg-white border border-gray-200 rounded-xl overflow-visible">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-[10.5px] font-semibold tracking-wider uppercase text-gray-500"><tr><th className="text-left px-3 py-2">Rubro de origen</th><th className="text-left px-3 py-2 w-24">Líneas</th><th className="text-left px-3 py-2 w-[38%]">Cuenta</th>{catalogo.centros.some(c => c.movimiento) && <th className="text-left px-3 py-2 w-[22%]">Centro de costo</th>}</tr></thead>
          <tbody className="divide-y divide-gray-100">
            {rubros.map(o => {
              const ls = lineasDe(o);
              return (
                <tr key={o}>
                  <td className="px-3 py-2"><div className="font-medium text-gray-900">{o}</div>{ls.length > 0 && <div className="text-xs text-gray-500 truncate max-w-md" title={ls.map(l => l.nombre).join(', ')}>{ls.map(l => l.nombre).join(', ')}</div>}</td>
                  <td className="px-3 py-2 text-gray-600">{ls.length || info.pendientes.find(p => p.origen === o)?.lineas || 0}</td>
                  <td className="px-3 py-2"><SelectorCuenta cuentas={catalogo.cuentas} value={elecciones[o]?.cuentaId} onChange={(id) => elegir(o, { cuentaId: id })} disabled={!puedeEditar} compacto /></td>
                  {catalogo.centros.some(c => c.movimiento) && <td className="px-3 py-2"><SelectorCentro centros={catalogo.centros} value={elecciones[o]?.centroCostoId} onChange={(id) => elegir(o, { centroCostoId: id })} disabled={!puedeEditar} compacto /></td>}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {puedeEditar && (
        <div className="flex items-center gap-3">
          <button onClick={guardar} disabled={guardando || !listos} className="inline-flex items-center gap-1 px-4 py-2 border-2 border-indigo-500 text-indigo-600 rounded-lg hover:bg-indigo-50 text-sm font-medium disabled:opacity-50">{guardando ? <Loader2 size={16} className="animate-spin" /> : <Link2 size={16} />} Guardar y aplicar ({listos} de {rubros.length})</button>
          <span className="text-xs text-gray-500">Los rubros que deje sin cuenta siguen pendientes.</span>
        </div>
      )}
    </div>
  );
}
